"use client";
import React, { useState, useRef, useEffect } from 'react';
import { Star, Heart, ChevronDown, ChevronRight, MoreHorizontal, ChevronLeft } from 'lucide-react';
import Image from 'next/image';
import avatar from '../../../../public/images/service/avatar1.png';
import api, { API_BASE_URL } from '@/config/api';

const ServicesHome = () => {
    const [activeTab, setActiveTab] = useState('');
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [visibleElements, setVisibleElements] = useState(new Set());
    const [isInitialized, setIsInitialized] = useState(false);
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [cardsPerSlide, setCardsPerSlide] = useState(4);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const tabRefs = useRef({});
    const containerRef = useRef(null);
    const headerRef = useRef(null);
    const tabsRef = useRef(null);
    const cardsRef = useRef([]);
    const moreButtonRef = useRef(null);
    const carouselRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    const autoPlayIntervalRef = useRef(null);

    // Responsive cardsPerSlide based on screen size
    useEffect(() => {
        const updateCardsPerSlide = () => {
            if (window.innerWidth < 640) { // Mobile
                setCardsPerSlide(1);
            } else if (window.innerWidth < 768) { // Small tablets
                setCardsPerSlide(2);
            } else if (window.innerWidth < 1024) { // Tablets
                setCardsPerSlide(3);
            } else { // Desktop
                setCardsPerSlide(4);
            }
        };

        updateCardsPerSlide();
        window.addEventListener('resize', updateCardsPerSlide);
        return () => window.removeEventListener('resize', updateCardsPerSlide);
    }, []);

    // Filter services based on active tab
    const filteredServices = activeTab === 'All Services'
        ? services
        : services.filter(service => service.category === activeTab);

    // Auto-scroll functionality
    useEffect(() => {
        if (!isAutoPlaying || isPaused || !filteredServices || filteredServices.length === 0) {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
                autoPlayIntervalRef.current = null;
            }
            return;
        }

        autoPlayIntervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => {
                const maxSlides = Math.ceil(filteredServices.length / cardsPerSlide) - 1;
                return prev < maxSlides ? prev + 1 : 0;
            });
        }, 4000); // Auto-scroll every 4 seconds

        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, [isAutoPlaying, isPaused, filteredServices, cardsPerSlide]);

    // Fetch data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await api.get('/getSiteData');
                const data = response.data;

                console.log('API Response:', data);

                if (data && data.sc_list && data.se_list) {
                    // Set categories from sc_list, skip the first item
                    const transformedCategories = data.sc_list.slice(1).map(category => ({
                        id: category.sc_id,
                        name: category.sc_name
                    }));

                    // Add "All Services" option at the beginning
                    const allServicesCategory = { id: 'all', name: 'All Services' };
                    setCategories([allServicesCategory, ...transformedCategories]);

                    // Set active tab to "All Services"
                    setActiveTab(allServicesCategory.name);

                    // Transform services from se_list, skip first item and filter out "Other" service (se_id: 1)
                    const transformedServices = data.se_list
                        .slice(1) // Skip the first item
                        .filter(service => service.se_id !== 1) // Exclude "Other" service
                        .map((service, index) => {
                            // Find the category name for this service
                            const category = data.sc_list.find(cat => cat.sc_id === service.se_sc_id);
                            const categoryName = category ? category.sc_name : 'Other';

                            // Default image for hyrelancer
                            const hyrelancerDefault = '/images/hyrelancer-dif.png';
                            const baseUrl = API_BASE_URL.replace('/api', '');

                            return {
                                id: service.se_id,
                                slug: service.se_slug,
                                category: categoryName,
                                title: service.se_name || `${categoryName} service`,
                                description: service.se_desc || '',
                                rating: 4.5 + (Math.random() * 0.5), // Random rating between 4.5-5.0
                                reviews: Math.floor(Math.random() * 100) + 1, // Random reviews count
                                author: `User${service.se_id}`,
                                price: `₹${(Math.random() * 100 + 20).toFixed(2)}`,
                                image: (service.se_img && service.se_img !== "" && !service.se_img.includes('default-img.png'))
                                    ? `${baseUrl}/${service.se_img.split('--')[0]}`
                                    : hyrelancerDefault,
                                bgColor: 'bg-gradient-to-br from-blue-400 to-purple-500' // Default gradient
                            };
                        });

                    setServices(transformedServices);
                    setError(null);
                } else {
                    console.error('Invalid data structure:', data);
                    throw new Error('Invalid data structure received from API');
                }
            } catch (err) {
                console.error('Error fetching services:', err);
                setError(`Failed to load services: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Get visible tabs (first 3 + "More" button)
    const visibleTabs = categories.slice(0, 3);
    const hiddenTabs = categories.slice(1);

    // Initialize after component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Intersection Observer for scroll animations
    useEffect(() => {
        if (!isInitialized || isLoading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisibleElements((prev) => new Set([...prev, entry.target.dataset.animateId]));
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -100px 0px', // Trigger when element is 100px from bottom of viewport
            }
        );

        // Observe header
        if (headerRef.current) {
            observer.observe(headerRef.current);
        }

        // Observe subheading
        const subheaderEl = document.querySelector('[data-animate-id="subheader"]');
        if (subheaderEl) {
            observer.observe(subheaderEl);
        }

        // Observe description
        const descriptionEl = document.querySelector('[data-animate-id="description"]');
        if (descriptionEl) {
            observer.observe(descriptionEl);
        }

        // Observe tabs
        if (tabsRef.current) {
            observer.observe(tabsRef.current);
        }

        // Observe cards
        cardsRef.current.forEach((card) => {
            if (card) {
                observer.observe(card);
            }
        });

        return () => observer.disconnect();
    }, [isInitialized, filteredServices, isLoading]);

    // Update tab indicator
    useEffect(() => {
        const updateIndicator = () => {
            const activeButton = tabRefs.current[activeTab];
            const container = containerRef.current;

            if (activeButton && container) {
                const containerRect = container.getBoundingClientRect();
                const buttonRect = activeButton.getBoundingClientRect();

                setIndicatorStyle({
                    left: buttonRect.left - containerRect.left,
                    width: buttonRect.width
                });
            }
        };

        const timer = setTimeout(updateIndicator, 10);
        window.addEventListener('resize', updateIndicator);

        // Close dropdowns when clicking outside
        const handleClickOutside = (event) => {
            if (moreButtonRef.current && !moreButtonRef.current.contains(event.target)) {
                setIsMoreDropdownOpen(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setIsCategoryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('resize', updateIndicator);
            document.removeEventListener('mousedown', handleClickOutside);
            clearTimeout(timer);
        };
    }, [activeTab, categories]);

    // Reset card animations when tab changes
    useEffect(() => {
        setVisibleElements(prev => {
            const newSet = new Set(prev);
            // Keep header and tabs visible if they were already visible
            if (prev.has('header')) newSet.add('header');
            if (prev.has('subheader')) newSet.add('subheader');
            if (prev.has('description')) newSet.add('description');
            if (prev.has('tabs')) newSet.add('tabs');

            // Remove all card animations
            services.forEach(service => {
                newSet.delete(`card-${service.id}`);
            });
            return newSet;
        });
        setCurrentSlide(0); // Reset carousel to first slide
    }, [activeTab]);

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 py-12 px-4 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Services</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-[#3a599c] text-white rounded-md hover:bg-[#344e86] transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white py-16 px-4">
                <div className=" mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-sm font-medium text-[#3a599c] mb-2">Boost Your Working Flow</p>
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Featured Services</h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Discover our featured services designed to elevate your experience
                        </p>
                    </div>
                    <div className="flex justify-center mb-12">
                        <div className="flex gap-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="animate-pulse bg-gray-300 h-12 w-24 rounded-full"></div>
                            ))}
                        </div>
                    </div>
                    <div className="relative">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                                    <div className="animate-pulse bg-gray-300 h-48 w-full"></div>
                                    <div className="p-4">
                                        <div className="animate-pulse bg-gray-300 h-4 w-1/3 mb-3 rounded"></div>
                                        <div className="animate-pulse bg-gray-300 h-6 w-full mb-2 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Carousel navigation functions
    const nextSlide = () => {
        const maxSlides = Math.ceil(filteredServices.length / cardsPerSlide) - 1;
        setCurrentSlide(prev => {
            const next = prev < maxSlides ? prev + 1 : 0;
            return next;
        });
        // Pause auto-play temporarily when manually navigating
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 6000); // Resume after 6 seconds
    };

    const prevSlide = () => {
        const maxSlides = Math.ceil(filteredServices.length / cardsPerSlide) - 1;
        setCurrentSlide(prev => {
            const prevSlide = prev > 0 ? prev - 1 : maxSlides;
            return prevSlide;
        });
        // Pause auto-play temporarily when manually navigating
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 6000); // Resume after 6 seconds
    };

    // Touch handlers for swipe
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsPaused(true); // Pause auto-play while swiping
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }

        // Resume auto-play after swipe
        setTimeout(() => setIsPaused(false), 6000);
    };

    return (
        <div className="min-h-screen bg-white py-16 px-4">
            <div className=" mx-auto" style={{ maxWidth: "1550px" }}>
                {/* Header */}
                <div className="text-center mb-12">
                    <p
                        ref={headerRef}
                        data-animate-id="header"
                        className={`text-sm font-medium text-[#3a599c] mb-2 transition-all duration-700 ease-out ${visibleElements.has('header')
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                            }`}
                        style={{ transitionDelay: '100ms' }}
                    >
                        Boost Your Working Flow
                    </p>
                    <h2
                        data-animate-id="subheader"
                        className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 ease-out ${visibleElements.has('subheader')
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                            }`}
                        style={{ transitionDelay: '250ms' }}
                    >
                        Featured Services
                    </h2>
                    <p
                        data-animate-id="description"
                        className={`text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-700 ease-out ${visibleElements.has('description')
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                            }`}
                        style={{ transitionDelay: '400ms' }}
                    >
                        Discover our featured services designed to elevate your experience
                    </p>
                </div>

                {/* Filter Buttons */}
                {categories.length > 0 && (
                    <div
                        ref={tabsRef}
                        data-animate-id="tabs"
                        className={`flex justify-center mb-12 transition-all duration-700 ease-out relative ${visibleElements.has('tabs')
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                            }`}
                        style={{
                            transitionDelay: '500ms',
                            zIndex: isCategoryDropdownOpen ? 9999 : 1
                        }}
                    >
                        {/* Desktop View - Button Grid */}
                        <div className="hidden md:flex flex-wrap justify-center gap-3">
                            {categories.slice(0, 1).concat(categories.slice(2, 6)).map((category) => (
                                <button
                                    key={category.id}
                                    ref={(el) => (tabRefs.current[category.name] = el)}
                                    onClick={() => setActiveTab(category.name)}
                                    className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap ${activeTab === category.name
                                        ? 'bg-[#3a599c] text-white shadow-md'
                                        : 'bg-white text-gray-700 border border-gray-300 hover:border-[#3a599c] hover:text-[#3a599c]'
                                        }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>

                        {/* Mobile View - Dropdown */}
                        <div ref={categoryDropdownRef} className="md:hidden w-full px-4 relative">
                            <button
                                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#3a599c] text-white rounded-full shadow-lg transition-all duration-300 hover:bg-[#2c4a7a] focus:outline-none focus:ring-2 focus:ring-[#3a599c] focus:ring-offset-2"
                                aria-expanded={isCategoryDropdownOpen}
                                aria-haspopup="true"
                            >
                                <span className="font-semibold text-base tracking-wide truncate">
                                    {activeTab || "Select Category"}
                                </span>
                                <svg
                                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {isCategoryDropdownOpen && (
                                <div
                                    className="absolute top-full left-4 right-4 mt-3 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl overflow-y-auto max-h-[400px] z-[9999]"
                                    style={{
                                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                    }}
                                >
                                    {categories.slice(1, 10).map((category, index) => (
                                        <button
                                            key={category.id}
                                            onClick={() => {
                                                setActiveTab(category.name);
                                                setIsCategoryDropdownOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all duration-200 ${index !== categories.slice(1, 5).length - 1 ? 'border-b border-gray-100' : ''
                                                } ${activeTab === category.name
                                                    ? "bg-[#3a599c] bg-opacity-10 text-[#3a599c] font-semibold"
                                                    : "text-gray-700 hover:bg-gray-50 hover:text-[#3a599c]"
                                                }`}
                                        >
                                            <span className="font-medium text-base">
                                                {category.name}
                                            </span>
                                            {activeTab === category.name && (
                                                <svg className="w-5 h-5 text-[#3a599c] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Service Cards Carousel */}
                {filteredServices.length > 0 ? (
                    <div
                        className="relative px-2 sm:px-4 md:px-8 lg:px-12"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                    >
                        {/* Navigation Arrows */}
                        <button
                            onClick={prevSlide}
                            className="absolute left-0 sm:left-1 md:left-2 lg:left-0 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-2.5 md:p-3 lg:p-4 bg-white rounded-full shadow-xl hover:shadow-2xl hover:bg-gray-50 active:scale-95 transition-all duration-300 border-2 border-gray-200 hover:border-[#3a599c] group"
                            aria-label="Previous slide"
                        >
                            <ChevronLeft className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-gray-700 group-hover:text-[#3a599c] transition-colors" />
                        </button>

                        <button
                            onClick={nextSlide}
                            className="absolute right-0 sm:right-1 md:right-2 lg:right-0 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-2.5 md:p-3 lg:p-4 bg-white rounded-full shadow-xl hover:shadow-2xl hover:bg-gray-50 active:scale-95 transition-all duration-300 border-2 border-gray-200 hover:border-[#3a599c] group"
                            aria-label="Next slide"
                        >
                            <ChevronRight className="w-5 h-5 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-gray-700 group-hover:text-[#3a599c] transition-colors" />
                        </button>

                        {/* Carousel Container */}
                        <div
                            ref={carouselRef}
                            className="overflow-hidden relative"
                        >
                            <div
                                className="flex transition-transform duration-500 ease-in-out"
                                style={{
                                    transform: `translateX(-${currentSlide * 100}%)`
                                }}
                            >
                                {/* Create slides with cards */}
                                {Array.from({ length: Math.ceil(filteredServices.length / cardsPerSlide) }, (_, slideIndex) => (
                                    <div
                                        key={slideIndex}
                                        className="w-full flex-shrink-0"
                                        style={{ maxWidth: "1500px", margin: "0 auto" }} // Match top component width
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 lg:gap-10 px-2 sm:px-4 md:px-6">
                                            {filteredServices.slice(slideIndex * cardsPerSlide, (slideIndex + 1) * cardsPerSlide).map((service, cardIndex) => {
                                                const globalIndex = slideIndex * cardsPerSlide + cardIndex;
                                                return (
                                                    <a
                                                        key={service.id}
                                                        href={service.slug ? `/servicelist/${service.slug}/freelancers` : '/servicelist'}
                                                        className="group block"
                                                    >
                                                        <div
                                                            ref={(el) => (cardsRef.current[globalIndex] = el)}
                                                            data-animate-id={`card-${service.id}`}
                                                            className={`bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-700 ease-out relative z-10 ${visibleElements.has(`card-${service.id}`)
                                                                ? 'opacity-100 translate-y-0'
                                                                : 'opacity-0 translate-y-12'
                                                                }`}
                                                            style={{
                                                                transitionDelay: `${cardIndex * 150}ms`
                                                            }}
                                                        >
                                                            {/* Service Image */}
                                                            <div className="relative h-64 sm:h-56 md:h-52 lg:h-48 w-full rounded-lg p-2">
                                                                <div className="w-full h-full rounded-lg overflow-hidden">
                                                                    <Image
                                                                        src={service.image}
                                                                        alt={service.title}
                                                                        fill
                                                                        className="object-cover"
                                                                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                                                                        onError={(e) => {
                                                                            // Note: Next.js Image onError handler is different, 
                                                                            // but we can handle fallbacks in the src logic or a wrapper if needed.
                                                                            // For now, we'll trust the src logic we have.
                                                                        }}
                                                                    />
                                                                </div>
                                                                {/* Hover CTA Button */}
                                                                <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                                        <div className="px-4 py-2 bg-[#3a599c] text-white text-sm font-medium rounded-full shadow-lg">
                                                                            View Service
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Service Content */}
                                                            <div className="p-4 sm:p-5">
                                                                {/* Category */}
                                                                <div className="mb-2 sm:mb-3">
                                                                    <span className="text-xs sm:text-sm font-medium text-gray-500">{service.category}</span>
                                                                </div>

                                                                {/* Title */}
                                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight">
                                                                    {service.title}
                                                                </h3>
                                                            </div>
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-gray-600">No services found in this category.</p>
                    </div>
                )}
            </div>

            {services.length > 0 && (
                <div className="mt-10 flex justify-center">
                    <a
                        href="/servicelist"
                        className="inline-flex items-center px-5 py-2.5 bg-[#3a599c] text-white rounded-full text-sm font-medium hover:bg-[#344e86] transition-colors shadow-sm"
                    >
                        View All Services
                        <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
                    </a>
                </div>
            )}
        </div>
    );
};

export default ServicesHome;